import { FlattenPortalRequest, RequestWorkflowResponse } from '@/types/sso';
import useLanguage from '@/utils/hooks/useLanguage';
import { fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import Divider from '../common/Divider';
import FormStepper from '../form/components/FormStepper';
import WalletDetails from '../modules/Portal/Request/WalletDetails';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

interface PortalWalletDetailsModalProps extends ModalProps {
    selectedWallet?: FlattenPortalRequest;
    onClose: () => void;
}

const PortalWalletDetailsModal = (props: PortalWalletDetailsModalProps) => {
    const { opened, handlers, selectedWallet, onClose } = props;

    const lang = useLanguage();
    const { jwt, onFetchJWT } = useSso();

    const [tabActiveIndex, setTabActiveIndex] = useState(0);

    const workflowPayload = useMemo(() => {
        if (selectedWallet?.id) return { id: selectedWallet.id };
        else if (selectedWallet?.requests) {
            const approvedRequest = selectedWallet.requests.find(
                (r) => r.requestStatus === 'APPROVED' && r.requestType === 'WHITELIST_WALLET'
            );
            if (approvedRequest) return { id: approvedRequest.id };
        }
    }, [selectedWallet]);

    const workflowsApi = usePortalSWR<RequestWorkflowResponse[]>('/api/whitelist/workflows-wallets', () =>
        fetchPortalAPI(onFetchJWT, workflowPayload ? '/api/whitelist/workflows' : '', jwt, workflowPayload, 'POST')
    );

    const { data: workflowsData } = workflowsApi;

    useEffect(() => {
        if (opened) {
            if (selectedWallet?.id) workflowsApi.mutate();
            else workflowsApi.mutate([]);
        } else {
            setTabActiveIndex(0);
        }
    }, [opened]);

    const steps = useMemo(() => [{ key: 1, label: lang.navigation.overview }], [lang]);

    return (
        <Modal {...props} className="h-auto" size="max-w-lg">
            <ModalHeader>
                <ModalTitle>{lang.portal.walletAddressDetails}</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider neutral />
            <FormStepper.Main activeStep={tabActiveIndex} onStep={(step) => setTabActiveIndex(step)}>
                {steps.map(({ label }, index) => (
                    <FormStepper.Step key={label} step={index} label={label} />
                ))}
            </FormStepper.Main>
            <Tab.Group defaultIndex={0} selectedIndex={tabActiveIndex} onChange={(index) => setTabActiveIndex(index)}>
                <Tab.List className={'hidden'}>
                    {steps.map(({ label }, index) => (
                        <Tab key={label} className="hidden">
                            {label}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className="pb-4 w-full h-[500px] overflow-y-auto px-2 sm:px-4">
                    <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                        <WalletDetails.Section1 selectedWallet={selectedWallet} workflowsData={workflowsData} />
                    </Tab.Panel>
                    {/* <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                            <WalletDetails.Section2 selectedWallet={selectedWallet} workflowsData={workflowsData} />
                        </Tab.Panel> */}
                </Tab.Panels>
            </Tab.Group>
            <Divider neutral />
            <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                <button
                    type="button"
                    className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                    onClick={() => {
                        onClose();
                    }}>
                    {lang.common.close}
                </button>
            </div>
        </Modal>
    );
};

export default PortalWalletDetailsModal;
